import React from 'react';
import { motion } from 'motion/react';
import { Shield, Lock, Eye, Activity, RefreshCw, CheckCircle2, AlertTriangle, FileText } from 'lucide-react';

export default function SecurityPolicy() {
  const lastUpdated = "April 21, 2026";

  const securityPillars = [
    {
      title: "Access Control",
      description: "System access is restricted based on role and business need. Administrative access is limited to authorized personnel only, utilizing multi-factor authentication and principle of least privilege.",
      icon: Lock
    },
    {
      title: "Data Protection",
      description: "All data in transit is encrypted using modern protocols (TLS 1.3+). Sensitive information is handled with strict confidentiality and stored with AES-256 encryption at rest.",
      icon: Shield
    },
    {
      title: "System Security",
      description: "Systems are continuously monitored for vulnerabilities and misconfigurations. We perform regular automated scans and periodic third-party penetration testing.",
      icon: Eye
    },
    {
      title: "Incident Response",
      description: "Security incidents are identified, investigated, and addressed promptly to minimize impact. Our team maintains a rapid-response protocol for all identified threats.",
      icon: Activity
    },
    {
      title: "Continuous Improvement",
      description: "Security controls are reviewed and updated regularly to reflect evolving threats and business needs, aligning with frameworks like PCI DSS, SOC 2, and NIST.",
      icon: RefreshCw
    }
  ];

  return (
    <div className="min-h-screen bg-background pt-32 pb-20">
      <div className="container mx-auto px-4 md:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto space-y-12"
        >
          {/* Header */}
          <div className="space-y-4 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[10px] font-bold uppercase tracking-[0.3em]">
              Information Security
            </div>
            <h1 className="text-4xl md:text-6xl font-display font-bold tracking-tight">
              Security <span className="text-accent">Policy</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Agrandintl is committed to protecting the confidentiality, integrity, and availability of all client and business information.
            </p>
          </div>

          {/* Core Commitments Card */}
          <div className="glass-card p-8 md:p-12 rounded-[2.5rem] border-white/5 bg-white/[0.01] relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-accent to-transparent opacity-20" />
            <div className="space-y-6 relative z-10">
              <div className="flex items-center gap-3 text-accent">
                <FileText className="w-6 h-6" />
                <h2 className="text-2xl font-display font-bold">Policy Statement</h2>
              </div>
              <div className="space-y-4 text-muted-foreground leading-relaxed text-base">
                <p>
                  Agrandintl is committed to protecting the confidentiality, integrity, and availability of all client and business information. 
                  We implement security controls aligned with industry best practices, including frameworks such as PCI DSS, SOC 2, and general cybersecurity standards.
                </p>
                <p>
                  Our security program is designed to provide a layered defense, ensuring that every touchpoint of our technology and consulting infrastructure is resilient against modern threats.
                </p>
              </div>
            </div>
          </div>

          {/* Pillars Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {securityPillars.map((pillar, idx) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card p-8 rounded-3xl border-white/5 bg-white/[0.01] hover:border-accent/20 transition-all group"
              >
                <div className="w-12 h-12 rounded-2xl bg-white/5 flex items-center justify-center text-accent mb-6 group-hover:scale-110 transition-transform">
                  <pillar.icon className="w-6 h-6" />
                </div>
                <h3 className="text-xl font-display font-bold mb-3">{pillar.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {pillar.description}
                </p>
              </motion.div>
            ))}
          </div>

          {/* Summary / Compliance */}
          <div className="pt-12 border-t border-white/5 text-center space-y-6">
            <div className="flex flex-wrap justify-center gap-8 opacity-40 grayscale hover:grayscale-0 transition-all duration-500">
              <div className="flex items-center gap-2 font-display font-bold text-lg tracking-tighter">
                <CheckCircle2 className="w-5 h-5 text-accent" />
                PCI DSS COMPLIANT
              </div>
              <div className="flex items-center gap-2 font-display font-bold text-lg tracking-tighter">
                <CheckCircle2 className="w-5 h-5 text-accent" />
                SOC 2 READY
              </div>
              <div className="flex items-center gap-2 font-display font-bold text-lg tracking-tighter">
                <CheckCircle2 className="w-5 h-5 text-accent" />
                ISO 27001 ALIGNED
              </div>
            </div>
            
            <p className="text-muted-foreground text-sm max-w-xl mx-auto italic">
              Agrandintl maintains a security-first approach in all services and operations. We continuously review our landscape to defend against evolving threats.
            </p>
            
            <div className="pt-8 text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground/60">
              Last Updated: {lastUpdated}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
